<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog"
      scrollable
      fullscreen
    >
      <v-card tile>
        <v-card-title>
          <span>
          Physical Count "{{ physicalCountList ? physicalCountList.name : '' }}, as of {{ physicalCountList ? physicalCountList.date : '' }}"
          </span>
          <v-spacer/>
          <v-col cols="auto">
            <span>
              <span style="font-weight: bold;">
              Total Before:
              </span>
              {{ totalOriginalQuantity }}
            </span>
            <span>
              <span style="font-weight: bold;">
              Total Counted:
              </span>
              {{ totalCountedQuantity }}
            </span>
            <span>
              <span style="font-weight: bold;">
                Total Variance by Qty: 
              </span>
              {{ totalVarianceByQuantity.toFixed(2) }}%
            </span>
            <span>
              <span style="font-weight: bold;">
                Total Variance per tag: 
              </span>
              {{ totalVariancePerTag.toFixed(2) }}%
            </span>
          </v-col>
          <v-col cols="auto" class="text-right">
            <v-btn
              icon
              @click="dialog = false"
            >
              <v-icon
                large
                dark
              >
                mdi-close
              </v-icon>
            </v-btn>
          </v-col>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col
              cols="3"
              class="mb-3"
            >
              <v-text-field
                v-model="search"
                append-icon="fas fa-search"
                label="Search"
                single-line
                hide-details
              />
            </v-col>
            <v-col
              cols="2"
              class="mb-3"
            >
              <inventory-class-select-element
                v-model="inventoryClassId"
                :is-add-all="false"
                :is-state="true"
              />
            </v-col>
            <v-col
              cols="3"
              class="mb-3"
            >
              <v-autocomplete
                v-model="location"
                label="Location"
                :items="locations"
                item-text="name"
                item-value="id"
                :clearable="true"
              />
            </v-col>
          </v-row>
          <v-data-table
            class="elevation-1"
            :headers="profilesHeaders"
            :items="physicalCounts"
            :item-class="itemClass"
            :items-per-page="pagination.itemsPerPage"
            :footer-props="footerProps"
            :loading="loading"
            :search="search"
            :height="windowHeight"
            fixed-header
            dense
          >
            <template #[`header.quantity`]="{ header }">
              <span>
                {{ header.text }}
                <v-tooltip top>
                  <template #activator="{ on, attrs }">
                    <v-icon
                      small
                      color="white"
                      class="ml-1"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-information
                    </v-icon>
                  </template>

                  <span>
                    Quantity after adjustments have been made from STL QR and Physical Count QR
                  </span>
                </v-tooltip>
              </span>
            </template>
            <template #[`item.inventory_tag`]="{ item }">
              <text-highlight
                class="font-weight-bold"
                :queries="search"
              >
                {{ item ? item.inventory_tag : '' }}
              </text-highlight>
            </template>
            <template #[`item.item_name`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item ? item.item_name : '' }}
              </text-highlight>
            </template>
            <template #[`item.length`]="{ item }">
              {{ item && item.length ? item.length.name : '' }}
            </template>
            <template #[`item.uom`]="{ item }">
              {{ item && item.uom ? item.uom.name : '' }}
            </template>
            <template #[`item.location`]="{ item }">
              {{ item && item.location ? item.location.name : '' }}
            </template>
            <template #[`item.color_code`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item ? item.color_code : '' }}
              </text-highlight>
            </template>
            <template #[`item.is_fritted`]="{ item }">
              {{ item.is_fritted === null ? '-' : item.is_fritted ? 'Fritted' : 'Non-Fritted' }}
            </template>
            <template #[`item.inventory_location`]="{ item }">
              <text-highlight
                :queries="search"
              >
                {{ item && item.inventory_location ? item.inventory_location.name : '' }}
              </text-highlight>
            </template>
            <template #[`item.variance`]="{ item }">
              <span :style="{ color: item ? tagVarianceColor(item) : 'black' }">
                {{ item ? tagVariance(item).toFixed(2) + '%' : '-' }}
              </span>
            </template>
            <template #[`item.quantity`]="{ item }">
              {{ item ? item.quantity : '-' }}
            </template>
            <template #[`item.created_at`]="{ item }">
              {{ item ? item.created_at : '' }}
            </template>
            <template #[`item.created_by`]="{ item }">
              {{ item ? item.user_full_name : '' }}
            </template>
          </v-data-table>
          <v-row>
            <v-col cols="8" />
            <v-col
              class="text-end mt-2"
              cols="2"
            >
              <h3
                :style="{color: isDarkMode ? '' : 'black'}"
                class="font-weight-bold"
              >
                Total Records: {{ totalRecords }}
              </h3>
            </v-col>
            <v-col
              class="text-end mt-2"
              cols="2"
            >
              <h3
                :style="{color: isDarkMode ? '' : 'black'}"
                class="font-weight-bold"
              >
                Total Quantity: {{ totalQuantity }}
              </h3>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapState } from 'vuex';
import TextHighlight from 'vue-text-highlight';

export default {
  components: {
    TextHighlight,
  },
  props:
  {
    physicalCountList: {
      type: Object,
      default: () => null,
    },
  },
  data: () => ({
    physicalCounts: [],
    locations: [],

    loading: false,
    dialog: false,
    search: '',
    location: null,
    totalRecords: 0,
    inventoryClassId: 1,

    inventoryClassIdGlass: 5,
    inventoryClassIdVWDGlass: 7,
    inventoryClassIdPanels: 8,
    inventoryClassIdSID: 9,
    inventoryClassIdPanelParts: 10,
    inventoryClassIdPaint: 12,
    inventoryClassIdPatioDoor: 14,

    topSpaceHeight: 280,
    windowHeight: 1000,
    pagination: {
      itemsPerPage: 100,
      page: 1,
      sortBy: 'physical_counts.id',
      sortDesc: true,
    },
    footerProps: {
      'items-per-page-options': [5, 10, 25, 50, 100, 500, 1000, -1],
    },

  }),
  computed: {
    profilesHeaders() {
      const headers = [];
      headers.push({ text: 'QR Code', value: 'inventory_tag', sortable: false });
      headers.push({ text: 'Inventory Item', value: 'item_name', sortable: false });

      if(this.inventoryClassId !== this.inventoryClassIdSID && this.inventoryClassId !== this.inventoryClassIdGlass && this.inventoryClassId !== this.inventoryClassIdVWDGlass && this.inventoryClassId !== this.inventoryClassIdPanels && this.inventoryClassId !== this.inventoryClassIdPatioDoor) {
        headers.push({ text: 'Length', value: 'length', sortable: false });
      }

      headers.push({ text: 'UOM', value: 'uom', sortable: false });
      
      if(this.inventoryClassId !== this.inventoryClassIdSID && this.inventoryClassId !== this.inventoryClassIdGlass && this.inventoryClassId !== this.inventoryClassIdVWDGlass){
        headers.push({ text: this.inventoryClassId === this.inventoryClassIdPatioDoor ? 'Interior Color' : 'Color', value: 'color_code', sortable: false });
      }

      if (this.inventoryClassId === this.inventoryClassIdPatioDoor) {
        headers.push({
          text: 'Exterior Color', value: 'ex_color_code', filterable: false, sortable: false,
        });
        headers.push({
          text: 'Door Height', value: 'height', filterable: false, sortable: false,
        });
        headers.push({
          text: 'Door Width', value: 'width', filterable: false, sortable: false,
        });
        // headers.push({
        //   text: 'Glass', value: 'glass', filterable: false, sortable: false,
        // });
        headers.push({
          text: 'Fritted', value: 'is_fritted', filterable: false, sortable: false,
        });
        headers.push({
          text: 'Door Screen', value: 'door_screen.name', filterable: false, sortable: false,
        });
      }

      if (this.inventoryClassId === this.inventoryClassIdSID) {
        headers.push({
          text: 'Size', value: 'door_size.name', filterable: false, sortable: false,
        });
        headers.push({
          text: 'Hinge', value: 'door_hinge.name', filterable: false, sortable: false,
        });
        headers.push({
          text: 'Swing', value: 'door_swing.name', filterable: false, sortable: false,
        });
        headers.push({
          text: 'Lite', value: 'door_lite.name', filterable: false, sortable: false,
        });
        headers.push({
          text: 'In Color', value: 'color_code', filterable: false, sortable: false,
        });
        headers.push({
          text: 'Ex Color', value: 'ex_color_code', filterable: false, sortable: false,
        });
      }

      headers.push({ text: 'Location', value: 'location', sortable: false });
      headers.push({ text: 'Shelf', value: 'inventory_location', sortable: false });
      headers.push({ text: 'Original Quantity', value: 'original_quantity', align: 'center', sortable: true });
      headers.push({ text: 'Variance', value: 'variance', align: 'center', sortable: false });
      headers.push({ text: 'Counted Quantity', value: 'counted_quantity', align: 'center', sortable: true });
      headers.push({ text: 'Quantity', value: 'quantity', align: 'center', sortable: true });
      headers.push({ text: 'Created At', value: 'created_at', sortable: false });
      headers.push({ text: 'Created By', value: 'created_by', sortable: false });

      return headers;
    },
    ...mapState({
      isDarkMode: 'isDarkMode',
    }),
    totalQuantity() {
      return this.physicalCounts.reduce((sum, item) => sum + (item.quantity || 0), 0);
    },
    totalOriginalQuantity() {
      return this.physicalCounts.reduce((sum, item) => {
        return sum + Number(item.original_quantity || 0);
      }, 0);
    },

    totalCountedQuantity() {
      return this.physicalCounts.reduce((sum, item) => {
        return sum + Number(item.counted_quantity || 0);
      }, 0);
    },

    totalVarianceByQuantity() {
      const totalCounted = this.totalCountedQuantity;
      const totalOriginal = this.totalOriginalQuantity;

      if (totalOriginal === 0) return 0;

      return Math.abs(((totalCounted - totalOriginal) / totalOriginal) * 100);
    },

    totalVariancePerTag() {
      if (!this.physicalCounts || this.physicalCounts.length === 0) {
        return 0;
      }

      const variances = this.physicalCounts
        .map(item => {
          const counted = Number(item.counted_quantity || 0);
          const original = Number(item.original_quantity || 0);

          if (original === 0) return null;

          return Math.abs(((counted - original) / original) * 100);
        })
        .filter(v => v !== null);

      if (variances.length === 0) {
        return 0;
      }

      return variances.reduce((sum, v) => sum + v, 0) / variances.length;
    },
  },

  watch: {
    search() {
      this.getPhysicalCounts();
    },
    physicalCountList() {
      this.getPhysicalCounts();
    },
    inventoryClassId() {
      this.getPhysicalCounts();
    },
    location() {
      this.getPhysicalCounts();
    }
  },

  created() {
    this.windowHeight = window.innerHeight - this.topSpaceHeight;
    this.getLocations();
  },

  mounted() {
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    });
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },

  methods: {
    open() {
      this.dialog = true;
      this.getPhysicalCounts();
    },

    async getPhysicalCounts() {
      if (!this.physicalCountList) return;
      this.loading = true;
      this.physicalCounts = [];
      this.totalRecords = 0;

      const submitModel = {
        filters: {
          physical_count_list_id: this.physicalCountList.id,
          inventory_class_id: this.inventoryClassId,
          location_id: this.location,
        },
        includes: {
          set_to_zero_items: true,
        },
        search: this.search
      }

      const res = await this.$api.physicalCount.read(submitModel);
      this.physicalCounts = res.data;
      this.totalRecords = res.total;
      this.loading = false;
    },

    async getLocations() {
      const res = await this.$api.location.get();

      if (!res) return;
      this.locations = res;
    },

    tagVariance(item) {
      const counted = item.counted_quantity || 0;
      const original = item.original_quantity || 0;

      if (original === 0) return 0;

      return Math.abs(((counted - original) / original) * 100);
    },

    tagVarianceColor(item) {
      //if the variance is greater than 10, return red, else black
      const variance = this.tagVariance(item);
      if (variance > 10) {
        return 'red';
      }
      return 'black';
    },

    onResize() {
      this.windowHeight = window.innerHeight - this.topSpaceHeight;
    },

    updatePagination(newOptions) {
      if (newOptions.page !== this.pagination.page || newOptions.itemsPerPage !== this.pagination.itemsPerPage || newOptions.sortBy !== this.pagination.sortBy || newOptions.sortDesc !== this.pagination.sortDesc) {
        this.pagination = newOptions;
      }
    },
    
    itemClass(item) {
      return item && item.is_set_to_zero_item ? 'set-to-zero' : '';
    }
  },

};
</script>

<style scoped>
::v-deep .set-to-zero {
  background-color: #fff3e0 !important;
}

::v-deep .set-to-zero td {
  background-color: transparent !important;
}
</style>
